import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';

import { ChartFormatModel }         from '../typings/chart-format';
import { ChartFormatService }       from '../chart-format.service';
import { ChartInfoPanelModel }      from '../typings/chart-info-panel';
import { ChartPaddingModel }        from '../typings/chart-padding';
import { ChartPointModel }          from '../typings/chart-point';
import { ChartSeriesModel }         from '../typings/chart-series';
import { infoPanelPosition }        from '../chart-info-panel/chart-info-panel-position';
import { niceCeil, TICK_DIVISIONS } from '../chart-scale';

interface RenderedBar {
  color : string;
  height: number;
  width : number;
  x     : number;
  y     : number;
}

// Grouped or stacked vertical bars, one group per point, with a hover readout and a click per point
@Component({
  selector   : 'wor-bar-chart',
  styles: [ require('./bar-chart.component.scss') ],
  template: require('./bar-chart.component.html')
})
export class BarChartComponent implements OnChanges {
  @Input() format    : ChartFormatModel        = 'money';
  @Input() isStacked : boolean                 = false;
  @Input() points    : Array<ChartPointModel>  = [];
  @Input() series    : Array<ChartSeriesModel> = [];
  @Input() width     : number                  = 640;

  @Output() pointPressed : EventEmitter<ChartPointModel> = new EventEmitter<ChartPointModel>();

  public bars       : Array<RenderedBar>                     = [];
  public height     : number                                 = 260;
  public hiddenKeys : Array<string>                          = [];
  public hoverIndex : number                                 = -1;
  public infoPanel  : ChartInfoPanelModel                    = { isVisible: false, rows: [], title: '', x: 0, y: 0 };
  public padding    : ChartPaddingModel                      = { bottom: 26, left: 54, right: 14, top: 12 };
  public ticks      : Array<{ label : string; y : number; }> = [];
  public xLabels    : Array<{ text : string; x : number; }>  = [];

  private yMax : number = 1;

  constructor (
    private chartFormatService: ChartFormatService
  ) {}

  ngOnChanges () : void {
    // a series that is no longer supplied cannot stay hidden
    this.hiddenKeys = this.hiddenKeys.filter(key => this.series.some(item => item.key === key));

    this.render();
  }

  formatValue ( value : number ) : string {
    return this.chartFormatService.value(value, this.format);
  }

  hideInfoPanel () : void {
    this.hoverIndex = -1;
    this.infoPanel = { ...this.infoPanel, isVisible: false };
  }

  isDrillable () : boolean {
    return this.pointPressed.observed;
  }

  isHidden ( key : string ) : boolean {
    return this.hiddenKeys.indexOf(key) > -1;
  }

  onMove ( event : MouseEvent ) : void {
    if ( !this.points.length ) {
      return;
    }

    const svg = event.currentTarget as SVGSVGElement;
    const bounds = svg.getBoundingClientRect();
    const plotX = ( event.clientX - bounds.left ) * ( this.width / bounds.width );
    const innerWidth = this.width - this.padding.left - this.padding.right;
    const step = innerWidth / this.points.length;

    this.hoverIndex = Math.min(
      Math.max(Math.floor(( plotX - this.padding.left ) / step), 0),
      this.points.length - 1
    );

    const point = this.points[ this.hoverIndex ];

    this.infoPanel = {
      isVisible: true,
      rows     : this.visibleSeries().map(item => ({
        color: item.color,
        label: item.label,
        value: this.formatValue(point.values[ item.key ] || 0)
      })),
      title: point.label,
      ...infoPanelPosition(event, bounds)
    };
  }

  press () : void {
    if ( this.isDrillable() && this.hoverIndex >= 0 ) {
      this.pointPressed.emit(this.points[ this.hoverIndex ]);
    }
  }

  toggleSeries ( key : string ) : void {
    if ( !this.isHidden(key) && this.visibleSeries().length < 2 ) {
      return;
    }

    this.hiddenKeys = this.isHidden(key)
      ? this.hiddenKeys.filter(item => item !== key)
      : this.hiddenKeys.concat(key);

    this.render();
  }

  trackByIndex ( index : number ) : number {
    return index;
  }

  visibleSeries () : Array<ChartSeriesModel> {
    return this.series.filter(item => !this.isHidden(item.key));
  }

  private pointY ( value : number ) : number {
    const innerHeight = this.height - this.padding.top - this.padding.bottom;

    return this.padding.top + innerHeight - ( Math.max(value, 0) / this.yMax ) * innerHeight;
  }

  private render () : void {
    this.bars = [];
    this.hideInfoPanel();
    this.ticks = [];
    this.xLabels = [];

    if ( !this.points.length || !this.visibleSeries().length ) {
      return;
    }

    let max = 0;

    this.points.forEach(point => {
      if ( this.isStacked ) {
        max = Math.max(max, this.visibleSeries().reduce(( sum, item ) => sum + ( point.values[ item.key ] || 0 ), 0));
      } else {
        this.visibleSeries().forEach(item => max = Math.max(max, point.values[ item.key ] || 0));
      }
    });

    this.yMax = niceCeil(max);

    const innerWidth = this.width - this.padding.left - this.padding.right;
    const step = innerWidth / this.points.length;
    const groupWidth = Math.min(step * 0.7, 44);
    const baseY = this.pointY(0);

    this.points.forEach(( point, index ) => {
      const groupLeft = this.padding.left + index * step + ( step - groupWidth ) / 2;

      if ( this.isStacked ) {
        let running = 0;

        this.visibleSeries().forEach(item => {
          const value = point.values[ item.key ] || 0;
          const top = this.pointY(running + value);
          const bottom = this.pointY(running);

          this.bars.push({
            color : item.color,
            height: Math.max(bottom - top, 0),
            width : groupWidth,
            x     : groupLeft,
            y     : top
          });

          running += value;
        });
      } else {
        const barWidth = groupWidth / this.visibleSeries().length;

        this.visibleSeries().forEach(( item, seriesIndex ) => {
          const value = point.values[ item.key ] || 0;
          const top = this.pointY(value);

          this.bars.push({
            color : item.color,
            height: Math.max(baseY - top, 0),
            width : Math.max(barWidth - 2, 2),
            x     : groupLeft + seriesIndex * barWidth,
            y     : top
          });
        });
      }
    });

    for ( let index = 0; index <= TICK_DIVISIONS; index++ ) {
      const value = ( this.yMax / TICK_DIVISIONS ) * index;

      this.ticks.push({
        label: this.chartFormatService.axis(value, this.format),
        y    : this.pointY(value)
      });
    }

    const labelIndexes = this.points.length < 3
      ? this.points.map(( _point, index ) => index)
      : [ 0, Math.floor(( this.points.length - 1 ) / 2), this.points.length - 1 ];

    labelIndexes.forEach(index => {
      this.xLabels.push({
        text: this.points[ index ].label,
        x   : this.padding.left + index * step + step / 2
      });
    });
  }
}
